import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export interface HerculesDocument {
	fileName: string;
	name: string;
	extension: string;
	type: "Photo" | "Document" | "Invoice";
	binaryData?: Blob;
}

interface FileInfoDto {
	fileName: string;
	name: string;
	extension: string;
}

interface GetFileShareInfoResponse {
    files: FileInfoDto[];
}

const apiPathname = process.env.ONEVENT_API_PATHNAME || "";

function herculesUrl(action: string, consignmentNumber: string, extra?: string): string {
	const base = `${apiPathname}/hercules/${action}/${encodeURIComponent(consignmentNumber)}`;
	return extra ? `${base}/${encodeURIComponent(extra)}` : base;
}

async function fetchFileShareInfo(
	consignmentNumber: string,
	type: "Photo" | "Document"
): Promise<HerculesDocument[]> {
	const action = type === "Photo" ? "GetPhotoInfo" : "GetDocumentInfo";
	const response = await fetch(herculesUrl(action, consignmentNumber));

	if (!response.ok) {
		throw new Error(`Failed to fetch ${type} info: ${response.status} ${response.statusText}`);
	}

	const data: GetFileShareInfoResponse = await response.json();
	return (data.files ?? []).map((file) => ({
		fileName: file.fileName ?? "",
		name: file.name ?? file.fileName ?? "",
		extension: file.extension ?? "",
		type
	}));
}

function getFileNameFromContentDisposition(contentDisposition: string | null, fallbackName = "Invoice.pdf"): string {
	if (!contentDisposition) {
		return fallbackName;
	}

	const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
	const encoded = match?.[1];
	const plain = match?.[2];

	if (encoded) {
		return decodeURIComponent(encoded);
	}

	return plain || fallbackName;
}

function openBlobInNewTab(blob: Blob) {
	const blobUrl = URL.createObjectURL(blob);
	const openedTab = window.open(blobUrl, "_blank");

	if (!openedTab) {
		URL.revokeObjectURL(blobUrl);
		throw new Error("Unable to open a new tab. Please allow pop-ups and try again.");
	}

	setTimeout(() => {
		URL.revokeObjectURL(blobUrl);
	}, 60000);
}

async function fetchInvoice(consignmentNumber: string): Promise<HerculesDocument | null> {
	const response = await fetch(herculesUrl("GetInvoice", consignmentNumber));

	if (!response.ok) {
		throw new Error(`Failed to fetch Invoice: ${response.status} ${response.statusText}`);
	}

	const binaryData = await response.blob();
	const fullFileName = getFileNameFromContentDisposition(response.headers.get("content-disposition"));
	const lastDot = fullFileName.lastIndexOf(".");

	return {
		fileName: fullFileName,
		name: fullFileName,
		extension: lastDot >= 0 ? fullFileName.substring(lastDot) : "",
		type: "Invoice",
		binaryData
	};
}

async function fetchPrintableDocument(
	consignmentNumber: string,
	type: "Note" | "Label"
): Promise<Blob> {
	const action = type === "Note" ? "GetConsignmentNote" : "GetLabel";
	const response = await fetch(herculesUrl(action, consignmentNumber));

	if (!response.ok) {
		throw new Error(`${type === "Note" ? "Consignment Note" : "Label"} is not available.`);
	}

	return response.blob();
}

export function useHerculesDocuments(consignmentNumber?: string) {
	const enabled = !!consignmentNumber;

	const photosQuery = useQuery<HerculesDocument[], Error>({
		queryKey: ["herculesPhotos", consignmentNumber],
		queryFn: () => fetchFileShareInfo(consignmentNumber!, "Photo"),
		enabled
	});

	const documentsQuery = useQuery<HerculesDocument[], Error>({
		queryKey: ["herculesDocumentFiles", consignmentNumber],
		queryFn: () => fetchFileShareInfo(consignmentNumber!, "Document"),
		enabled
	});

	const invoiceQuery = useQuery<HerculesDocument | null, Error>({
		queryKey: ["herculesInvoice", consignmentNumber],
		queryFn: () => fetchInvoice(consignmentNumber!),
		enabled
	});

	const printConsignmentNoteMutation = useMutation<void, Error>({
		mutationFn: async () => {
			if (!consignmentNumber) {
				throw new Error("Print is unavailable. Consignment number is missing.");
			}

			const blob = await fetchPrintableDocument(consignmentNumber, "Note");
			openBlobInNewTab(blob);
		}
	});

	const printLabelMutation = useMutation<void, Error>({
		mutationFn: async () => {
			if (!consignmentNumber) {
				throw new Error("Print is unavailable. Consignment number is missing.");
			}

			const blob = await fetchPrintableDocument(consignmentNumber, "Label");
			openBlobInNewTab(blob);
		}
	});

	const files = useMemo(() => {
		const photos = photosQuery.data ?? [];
		const documents = documentsQuery.data ?? [];
		const invoice = invoiceQuery.data ?? null;
		return invoice ? [invoice, ...photos, ...documents] : [...photos, ...documents];
	}, [photosQuery.data, documentsQuery.data, invoiceQuery.data]);

	const isLoading = photosQuery.isLoading || documentsQuery.isLoading || invoiceQuery.isLoading;
	const isFetching = photosQuery.isFetching || documentsQuery.isFetching || invoiceQuery.isFetching;

	return {
		files,
		isLoading,
		isFetching,
		isPrintingConsignmentNote: printConsignmentNoteMutation.isLoading,
		isPrintingLabel: printLabelMutation.isLoading,
		printConsignmentNote: () => printConsignmentNoteMutation.mutateAsync(),
		printLabel: () => printLabelMutation.mutateAsync(),
		refetchDocuments: documentsQuery.refetch,
		refetchAll: () => Promise.all([photosQuery.refetch(), documentsQuery.refetch(), invoiceQuery.refetch()])
	};
}
